import React, { useState, useEffect } from "react";
import { apiJsonOrThrow, setTransientMessage } from "./adminUtils";
import LoadingButton from "../common/LoadingButton";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole(process.env.NODE_ENV === "development");
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

const EntityTypeAttributesManagement = ({
  user,
  hideHeader = false,
  mapLanguage = false,
}) => {
  // Get user from prop or localStorage as fallback
  const getCurrentUser = () => {
    if (user) return user;
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };
  
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [entityTypes, setEntityTypes] = useState([]);
  const [selectedEntityType, setSelectedEntityType] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);

  const [formData, setFormData] = useState({
    entity_type_id: "",
    attribute_name: "",
    attribute_label: "",
    data_type: "text",
    section: "",
    is_required: false,
    is_searchable: true,
    is_reportable: true,
    default_value: "",
    validation_rules: "",
    description: "",
    sort_order: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // Define loadAttributes first using useCallback
  const loadAttributes = React.useCallback(async (entityTypeId) => {
    if (!entityTypeId) {
      debugWarn("⚠️ loadAttributes called without entityTypeId");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      debugLog("🔄 Loading attributes for entity type:", entityTypeId);

      // Add cache-busting parameter to ensure fresh data
      const data = await apiJsonOrThrow(
        `/api/admin/entity-type-attributes?entity_type_id=${entityTypeId}&_t=${Date.now()}`
      );
      debugLog("✅ Attributes loaded:", data);
      debugLog("📊 Attributes count:", Array.isArray(data) ? data.length : 0);
      const attributesArray = Array.isArray(data) ? data : [];
      setAttributes(attributesArray);
      if (attributesArray.length === 0) {
        debugLog(
          "⚠️ No attributes found, but this might be expected for a new entity type"
        );
      }
    } catch (err) {
      debugError("❌ Error loading attributes:", err);
      setError(err.message);
      setAttributes([]);
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps since it only uses setState functions

  // Load entity types on mount - using static TYPE_PROFILE enum
  const loadEntityTypes = async () => {
    try {
      // Use static TYPE_PROFILE enum instead of API call
      const { getCatalogEntityTypes } = await import("../../utils/typeProfile");
      const typesList = getCatalogEntityTypes().map((profile) => ({
        id: profile.value, // Use value as ID since types are static
        name: profile.value,
        display_name: profile.label,
        icon: profile.icon,
        color: profile.color,
      }));
      setEntityTypes(typesList);
      // Auto-select first entity type if available
      if (typesList.length > 0 && !selectedEntityType) {
        setSelectedEntityType(typesList[0]);
      }
    } catch (err) {
      debugError("Error loading entity types:", err);
      setError(mapLanguage ? "Failed to load item types" : "Failed to load entity types");
    } finally {
      setLoading(false);
    }
  };

  // Update currentUser when user prop changes
  useEffect(() => {
    if (user) {
      setCurrentUser(user);
    } else {
      try {
        const userStr = localStorage.getItem("user");
        setCurrentUser(userStr ? JSON.parse(userStr) : null);
      } catch {
        setCurrentUser(null);
      }
    }
  }, [user]);

  useEffect(() => {
    loadEntityTypes();
  }, []);

  // Load attributes when entity type changes
  useEffect(() => {
    if (selectedEntityType && selectedEntityType.id) {
      debugLog(
        "🔄 Entity type changed, loading attributes:",
        selectedEntityType.id
      );
      loadAttributes(selectedEntityType.id);
    } else {
      debugLog("⚠️ No entity type selected, clearing attributes");
      setAttributes([]);
    }
  }, [selectedEntityType?.id, loadAttributes]); // Re-run when entity type ID or loadAttributes changes

  // Extract unique sections from attributes for the current entity type
  const existingSections = React.useMemo(() => {
    const sections = new Set();
    attributes.forEach((attr) => {
      if (attr.section && attr.section.trim()) {
        sections.add(attr.section.trim());
      }
    });
    return Array.from(sections).sort();
  }, [attributes]);

  // Group attributes by section
  const groupedAttributes = attributes.reduce((acc, attr) => {
    const section = attr.section || "General";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(attr);
    return acc;
  }, {});

  // Sort sections and attributes within sections
  const sortedSections = Object.keys(groupedAttributes).sort();
  sortedSections.forEach((section) => {
    groupedAttributes[section].sort(
      (a, b) =>
        a.sort_order - b.sort_order ||
        a.attribute_label.localeCompare(b.attribute_label)
    );
  });

  // State for custom section input
  const [showCustomSectionInput, setShowCustomSectionInput] =
    React.useState(false);

  const resetForm = () => {
    setFormData({
      entity_type_id: selectedEntityType?.id || "",
      attribute_name: "",
      attribute_label: "",
      data_type: "text",
      section: "",
      is_required: false,
      is_searchable: true,
      is_reportable: true,
      default_value: "",
      validation_rules: "",
      description: "",
      sort_order: 0,
    });
    setEditingAttribute(null);
    setShowCustomSectionInput(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is in trial mode
    if (currentUser?.is_trial_user && !currentUser?.is_super_user) {
      setError("This feature is restricted in trial mode");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage("");

    try {
      const payload = {
        entity_type_id: formData.entity_type_id,
        attribute_name: formData.attribute_name,
        attribute_label: formData.attribute_label,
        data_type: formData.data_type,
        section: formData.section || null,
        is_required: formData.is_required,
        is_searchable: formData.is_searchable,
        is_reportable: formData.is_reportable,
        default_value: formData.default_value || null,
        description: formData.description || null,
        sort_order: parseInt(formData.sort_order) || 0,
      };

      // Parse validation_rules if provided
      if (formData.validation_rules) {
        try {
          payload.validation_rules = JSON.parse(formData.validation_rules);
        } catch {
          throw new Error("Invalid JSON in validation rules");
        }
      }

      // Parse default_value based on data_type
      if (payload.default_value) {
        if (payload.data_type === "number") {
          payload.default_value = parseFloat(payload.default_value);
        } else if (payload.data_type === "boolean") {
          payload.default_value =
            payload.default_value === "true" || payload.default_value === true;
        }
      }

      const url = editingAttribute
        ? `/api/admin/entity-type-attributes/${editingAttribute.id}`
        : "/api/admin/entity-type-attributes";

      const method = editingAttribute ? "PUT" : "POST";

      await apiJsonOrThrow(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const message = editingAttribute
        ? "Attribute updated successfully!"
        : "Attribute created successfully!";
      setTransientMessage(setSuccessMessage, message, 3000);
      setShowCreateForm(false);
      resetForm();

      // Force refresh attributes list
      if (selectedEntityType && selectedEntityType.id) {
        debugLog("🔄 Refreshing attributes list after save...");
        await loadAttributes(selectedEntityType.id);
        debugLog("✅ Attributes list refreshed");
      } else {
        debugWarn("⚠️ Cannot refresh: selectedEntityType is not set");
        // Try to reload from entity_type_id in formData as fallback
        if (formData.entity_type_id) {
          debugLog("🔄 Using formData.entity_type_id as fallback");
          await loadAttributes(formData.entity_type_id);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (attribute) => {
    // Check if user is in trial mode
    if (currentUser?.is_trial_user && !currentUser?.is_super_user) {
      setError("This feature is restricted in trial mode");
      return;
    }
    
    setEditingAttribute(attribute);
    // Check if the section exists in existing sections, if not, show custom input
    const sectionExists = existingSections.includes(attribute.section || "");
    setShowCustomSectionInput(!sectionExists && attribute.section);
    setFormData({
      entity_type_id: attribute.entity_type_id,
      attribute_name: attribute.attribute_name,
      attribute_label: attribute.attribute_label,
      data_type: attribute.data_type,
      section: attribute.section || "",
      is_required: attribute.is_required,
      is_searchable: attribute.is_searchable,
      is_reportable: attribute.is_reportable,
      default_value: attribute.default_value
        ? typeof attribute.default_value === "object"
          ? JSON.stringify(attribute.default_value)
          : String(attribute.default_value)
        : "",
      validation_rules: attribute.validation_rules
        ? JSON.stringify(attribute.validation_rules, null, 2)
        : "",
      description: attribute.description || "",
      sort_order: attribute.sort_order || 0,
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (attributeId) => {
    // Check if user is in trial mode
    if (currentUser?.is_trial_user && !currentUser?.is_super_user) {
      setError("This feature is restricted in trial mode");
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this attribute?")) {
      return;
    }

    setDeletingId(attributeId);
    setError(null);

    try {
      await apiJsonOrThrow(`/api/admin/entity-type-attributes/${attributeId}`, {
        method: "DELETE",
      });
      setTransientMessage(setSuccessMessage, "Attribute deleted successfully!", 3000);
      await loadAttributes(selectedEntityType.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div
      className={
        hideHeader ? "bg-gray-900 p-4" : "min-h-screen bg-gray-900 p-6"
      }
    >
      <div className="max-w-7xl mx-auto">
        {!hideHeader ? (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">
              {mapLanguage ? "Custom fields" : "Entity Type Attributes"}
            </h1>
            <p className="text-gray-400">
              {mapLanguage
                ? "Extra fields shown on each item type, organized by section."
                : "Configure custom attributes for each entity type, organized by sections"}
            </p>
          </div>
        ) : null}

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-600/20 border border-green-600 rounded-lg text-green-300">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-600/20 border border-red-600 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* Entity Type Selector */}
        <div className="mb-6 bg-gray-800 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {mapLanguage ? "Item type" : "Select Entity Type"}
          </label>
          <select
            value={selectedEntityType?.id || ""}
            onChange={(e) => {
              const type = entityTypes.find((t) => t.id === e.target.value);
              setSelectedEntityType(type);
            }}
            disabled={loading}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">
              {mapLanguage ? "-- Select item type --" : "-- Select Entity Type --"}
            </option>
            {entityTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.display_name || type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Loading Screen */}
        {loading && (
          <div className="flex items-center justify-center h-64 bg-gray-800 rounded-lg">
            <div className="text-center">
              <svg
                className="animate-spin w-12 h-12 text-blue-500 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-gray-400">Loading attributes...</p>
            </div>
          </div>
        )}

        {selectedEntityType && !loading && (
          <>
            {/* Actions */}
            <div className="mb-6 flex items-center gap-3">
              {currentUser?.is_trial_user && !currentUser?.is_super_user ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-yellow-400">
                    This feature is restricted in trial mode
                  </span>
                  <button
                    disabled
                    className="px-4 py-2 bg-gray-600 text-gray-400 rounded-lg cursor-not-allowed opacity-50"
                  >
                    + Add Attribute
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    resetForm();
                    setFormData((prev) => ({
                      ...prev,
                      entity_type_id: selectedEntityType.id,
                    }));
                    setShowCustomSectionInput(false);
                    setShowCreateForm(true);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  + Add Attribute
                </button>
              )}
            </div>

            {/* Attributes by Section */}
            {sortedSections.length > 0 ? (
              <div className="space-y-6">
                {sortedSections.map((section) => (
                  <div
                    key={section}
                    className="bg-gray-800 rounded-lg overflow-hidden"
                  >
                    <div className="bg-gray-700 px-6 py-3 border-b border-gray-600">
                      <h2 className="text-xl font-semibold text-white">
                        {section}
                      </h2>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {groupedAttributes[section].map((attr) => (
                          <div
                            key={attr.id}
                            className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-blue-500 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="text-white font-medium">
                                  {attr.attribute_label}
                                </h3>
                                <p className="text-xs text-gray-400 mt-1">
                                  {attr.attribute_name} • {attr.data_type}
                                  {attr.is_required && (
                                    <span className="ml-2 px-1.5 py-0.5 bg-red-600/20 text-red-300 rounded text-xs">
                                      Required
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex gap-2 ml-2">
                                {currentUser?.is_trial_user && !currentUser?.is_super_user ? (
                                  <span className="text-xs text-yellow-400 px-2 py-1">
                                    Restricted in trial mode
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEdit(attr)}
                                      className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDelete(attr.id)}
                                      disabled={deletingId === attr.id}
                                      className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
                                    >
                                      {deletingId === attr.id ? "..." : "Delete"}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {attr.description && (
                              <p className="text-sm text-gray-400 mt-2">
                                {attr.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400">
                  No attributes configured yet. Click "Add Attribute" to get
                  started.
                </p>
              </div>
            )}
          </>
        )}

        {/* Create/Edit Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-2xl font-bold text-white">
                  {editingAttribute ? "Edit Attribute" : "Create Attribute"}
                </h2>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Entity Type (disabled if editing) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {mapLanguage ? "Item type" : "Entity Type"}
                  </label>
                  <select
                    value={formData.entity_type_id}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        entity_type_id: e.target.value,
                      }))
                    }
                    disabled={!!editingAttribute}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50"
                  >
                    <option value="">
              {mapLanguage ? "-- Select item type --" : "-- Select Entity Type --"}
            </option>
                    {entityTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.display_name || type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Section (Optional - for grouping)
                  </label>
                  {!showCustomSectionInput ? (
                    <div className="space-y-2">
                      <select
                        value={formData.section || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "__new__") {
                            setShowCustomSectionInput(true);
                            setFormData((prev) => ({ ...prev, section: "" }));
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              section: value,
                            }));
                          }
                        }}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="">-- No Section (General) --</option>
                        {existingSections.map((section) => (
                          <option key={section} value={section}>
                            {section}
                          </option>
                        ))}
                        <option value="__new__">+ Add New Section...</option>
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.section}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              section: e.target.value,
                            }))
                          }
                          placeholder="Enter new section name"
                          autoFocus
                          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomSectionInput(false);
                            setFormData((prev) => ({ ...prev, section: "" }));
                          }}
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Select an existing section or add a new one to organize
                    attributes
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Attribute Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Attribute Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.attribute_name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          attribute_name: e.target.value
                            .toLowerCase()
                            .replace(/\s+/g, "_"),
                        }))
                      }
                      placeholder="business_owner"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Lowercase with underscores
                    </p>
                  </div>

                  {/* Attribute Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Display Label *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.attribute_label}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          attribute_label: e.target.value,
                        }))
                      }
                      placeholder="Business Owner"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>

                {/* Data Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Data Type *
                  </label>
                  <select
                    required
                    value={formData.data_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        data_type: e.target.value,
                      }))
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="date">Date</option>
                    <option value="select">Select (Single)</option>
                    <option value="multi_select">Multi-Select</option>
                  </select>
                </div>

                {/* Validation Rules (for select, number, etc.) */}
                {(formData.data_type === "select" ||
                  formData.data_type === "multi_select") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Options (JSON) *
                    </label>
                    <textarea
                      required
                      value={formData.validation_rules}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          validation_rules: e.target.value,
                        }))
                      }
                      placeholder='{"options": ["Option 1", "Option 2", "Option 3"]}'
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono text-sm"
                    />
                  </div>
                )}

                {formData.data_type === "number" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Validation Rules (JSON, Optional)
                    </label>
                    <textarea
                      value={formData.validation_rules}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          validation_rules: e.target.value,
                        }))
                      }
                      placeholder='{"min": 0, "max": 100}'
                      rows={2}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono text-sm"
                    />
                  </div>
                )}

                {/* Default Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Default Value (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.default_value}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        default_value: e.target.value,
                      }))
                    }
                    placeholder={
                      formData.data_type === "boolean"
                        ? "true or false"
                        : "Default value"
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Brief description of this attribute"
                    rows={2}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>

                {/* Flags */}
                <div className="grid grid-cols-3 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_required}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_required: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">Required</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_searchable}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_searchable: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">Searchable</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_reportable}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_reportable: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">Reportable</span>
                  </label>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        sort_order: e.target.value,
                      }))
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                      setShowCustomSectionInput(false);
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <LoadingButton
                    type="submit"
                    loading={isSubmitting}
                    loadingLabel="Saving..."
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingAttribute ? "Update" : "Create"}
                  </LoadingButton>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityTypeAttributesManagement;
